<script lang="ts" setup>
import type { Course } from "~/types/Cours";
// If you have VueUse, uncomment this and use useDebounceFn.
// import { useDebounceFn } from '@vueuse/core'

const { getCourses } = useCoursApi();

/* ---------- state ---------- */
const courses = ref<Course[]>([]);
const loading = ref(false);
const loadingMore = ref(false);
const totalItem = ref(0);
const page = ref(1);

const PAGE_SIZE = 12;

/** All filters in one place */
const filters = reactive({
  category: "" as string,
  modality: "" as string,
  modulesLt: "" as string, // your "modules[lt]"
  durationLt: "" as string, // your "duration[lt]"
});

/* ---------- helpers ---------- */
const hasMore = computed(() => courses.value.length < totalItem.value);

/** Build query string from page + filters */
const buildQuery = () => {
  const p = new URLSearchParams();
  p.set("page", String(page.value));
  p.set("_per_page", String(PAGE_SIZE));

  if (filters.category && filters.category !== "Todos") {
    p.set("categoryName", filters.category);
  }
  if (filters.modality) p.set("modality", filters.modality);
  if (filters.modulesLt) p.set("modules[eq]", filters.modulesLt);
  if (filters.durationLt) p.set("duration", filters.durationLt);

  return p.toString();
};

/** A stable key representing only the filter values (not page) */
const filtersKey = computed(() =>
  JSON.stringify({
    category: filters.category,
    modality: filters.modality,
    modulesLt: filters.modulesLt,
    durationLt: filters.durationLt,
  })
);

/* ---------- fetching with race guard ---------- */
let lastRequestId = 0;

const fetchPage = async (append = false) => {
  const requestId = ++lastRequestId;

  if (append) loadingMore.value = true;
  else loading.value = true;

  try {
    const data = await getCourses(buildQuery());

    // ignore if a newer request finished after this one
    if (requestId !== lastRequestId) return;

    if (data?.member) {
      totalItem.value = data.totalItems ?? totalItem.value;
      courses.value = append ? [...courses.value, ...data.member] : data.member;
    }
  } finally {
    if (requestId === lastRequestId) {
      if (append) loadingMore.value = false;
      else loading.value = false;
    }
  }
};

/* ---------- react to filters (debounced) ---------- */

/** If you have VueUse, prefer this:
const triggerFetch = useDebounceFn(() => {
  page.value = 1;
  courses.value = [];
  fetchPage(false);
}, 250);
*/

let debounceTimer: number | null = null;
const triggerFetch = () => {
  if (debounceTimer) window.clearTimeout(debounceTimer);
  debounceTimer = window.setTimeout(() => {
    page.value = 1;
    courses.value = [];
    fetchPage(false);
  }, 250);
};

watch(filtersKey, () => {
  triggerFetch();
});

/* ---------- initial load ---------- */
onMounted(() => {
  fetchPage(false);
});

/* ---------- load more ---------- */
const loadMore = async () => {
  if (!hasMore.value || loadingMore.value) return;
  page.value += 1;
  await fetchPage(true);
};
const goToPage = (p: number) => {
  page.value = p;
  fetchPage(false);
};
</script>

<template>
  <FormacionesSection1
    :loading="loading"
    :totalItem="totalItem"
    :category="filters.category"
    @updateCategory="filters.category = $event"
    @updateModality="filters.modality = $event"
    @updateModule="filters.modulesLt = $event"
    @updateDuration="filters.durationLt = $event"
  />

  <UiLoading class="mt-8 mx-auto" v-if="loading" />
  <FormacionesAllCours
    v-else-if="totalItem > 0 && !loading"
    :courses="courses"
    :totalItem="totalItem"
    :loading="loading"
    :hasMore="hasMore"
    :loadingMore="loadingMore"
    :page="page"
    :pageCount="Math.ceil(totalItem / PAGE_SIZE)"
    @change-page="goToPage"
  />
  <p
    v-if="!loading && totalItem === 0"
    class="mt-8 text-center font-poppins text-2xl font-semibold"
  >
    No se encontraron cursos que coincidan con los filtros.
  </p>
</template>
